<template>
  <div>
    <va-modal
      v-model="showModal"
      :no-outside-dismiss="true"
      id="placingDetailsActionModal"
      ref="placingDetailsActionModal"
      :cancelText=" isEditing ? $t('modals.buttons.cancel') : $t('modals.buttons.back')"
      :deleteText="$t('modals.buttons.delete')"
      :okText="$t('modals.buttons.save')"
      size="medium"
      @cancel="handleCancel"
      @remove="handleDelete"
      @ok="handleOk"
      :fixedLayout="true"
      :hide-default-actions="true"
    >
      <div slot="header" class="modal-title mb-0">
        <span v-if="isEditing">{{`${$t('placings.edit_placing') } - ${selectedPlacing.name}`}}</span>
        <span v-if="isCreating">{{$t('placings.create_placing')}}</span>
        <span v-else>{{selectedPlacing.name}}</span>
      </div>

      <div class="modal-header-actions d-flex justify-content-end">
        <va-popover
          placement="left"
          :message="$t('placings.edit_placing')"
          v-if="!isEditing && !isCreating">
          <va-button
            large
            class="va-button-rounded"
            icon="fa fa-pencil"
            color="gray"
            @click="handleEdit">
          </va-button>
        </va-popover>
      </div>

      <form class="evollu-form">
        <div v-if="!isEditing && !isCreating">
          <h4>{{$t('placings.placing_name')}}</h4>
          <div class="row">
            <span class="ml-2">{{selectedPlacing.name}}</span>
          </div>

          <h4>{{$t('placings.coordinates')}}</h4>
          <div class="row" v-if="selectedPlacing.lat && selectedPlacing.lng">
            <span class="is-bold ml-2">{{$t('placings.latitude')}}:</span> &nbsp; <span>{{selectedPlacing.lat}}</span>
            &nbsp;
            <span class="is-bold">{{$t('placings.longitude')}}:</span> &nbsp; <span>{{selectedPlacing.lng}}</span>
          </div>
          <div class="row" v-else>
            <span class="ml-2">
              {{$t('placings.no_coordinates')}}
            </span>
          </div>

        </div>
        <div v-else>
          <div class="row" v-if="isCreating">
            <h5>{{$t('users.inputs.entity')}}</h5>
            <div class="col-12">
              <label class="form-label">{{ $t('users.inputs.entity') }}</label>
              <multiselect
                v-model="form.data.entity"
                :options="entities"
                id="placing-entity-input"
                track-by="id"
                label="name"
                :custom-label="formatedEntityLabel"
                select-label=""
                selected-label=""
                deselect-label="×"
                placeholder="---"
                :searchable="true"
                :allow-empty="true"
                :multiple="false"
                :limit="1"
                :limit-text="(count) => $t('tables.multiselect_limitText_and') + count + this.$t('tables.multiselect_limitText_others')"
                :close-on-select="true"
                @input="form.errors.clear('entity')"
              />
              <small v-show="form.errors.has('entity')" class="help text-danger">
                {{ $t('placings.invalid_entity') }}
              </small>
            </div>
          </div>
          <div class="row">
            <h5>{{$t('placings.placing_name')}}</h5>
            <div class="col-12">
              <label class="form-label">{{ $t('placings.placing_name') }}</label>
              <va-input
                id="placing-name-input"
                :placeholder="$t('placings.placing_name')"
                v-model="form.data.name"
                :maxlength="255"
                @input="form.errors.clear('name')"
              />
              <small v-show="form.errors.has('name')" class="help text-danger">
                {{ $t('placings.invalid_name') }}
              </small>
            </div>
          </div>
          <div class="row">
            <h5>{{$t('placings.coordinates')}}</h5>
            <div class="row col-12 pl-0 pr-0">
              <div class="col-6">
                <label class="form-label">{{ $t('placings.latitude') }}</label>
                <va-input
                  id="placing-lat-input"
                  :placeholder="$t('placings.latitude')"
                  v-model="form.data.lat"
                  :maxlength="15"
                  @input="form.errors.clear('lat')"
                />
                <small v-show="form.errors.has('lat')" class="help text-danger">
                  {{ $t('placings.invalid_latitude') }}
                </small>
              </div>
              <div class="col-6">
                <label class="form-label">{{ $t('placings.longitude') }}</label>
                <va-input
                  id="placing-lng-input"
                  :placeholder="$t('placings.longitude')"
                  v-model="form.data.lng"
                  :maxlength="15"
                  @input="form.errors.clear('lng')"
                />
                <small v-show="form.errors.has('lng')" class="help text-danger">
                  {{ $t('placings.invalid_longitude') }}
                </small>
              </div>
            </div>
          </div>
        </div>
        <div class="flex">
          <va-button flat outline @click="handleCancel">{{isEditing ? $t('modals.buttons.cancel') : $t('modals.buttons.back')}}</va-button>
          <va-button class="float-right" v-if="!isEditing && !isCreating" color="danger" @click="handleDelete">{{$t('modals.buttons.delete')}}</va-button>
          <va-button class="float-right" v-if="(isCreating || isEditing)" :disabled="!canSubmit" @click="handleOk">{{$t('modals.buttons.save')}}</va-button>
        </div>
      </form>
    </va-modal>
    <ConfirmationModal
      v-if="showConfirmationModal"
      :title="$t('placings.delete')"
      :body="$t('placings.delete_message', {name: selectedPlacing.name})"
      @modal-confirmed="handleDestroyPlacing"
      @modal-canceled="cancelDestroyPlacing" />
  </div>

</template>
<script>
import Errors from '@/utils/CustomNodeErrors.js'
import { PlacingsService } from '@/api/LeadsService'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import Multiselect from 'vue-multiselect'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'

export default {
  mixins: [
    MultiselectLabelFormatter,
  ],
  components: { ConfirmationModal, Multiselect },
  props: {
    selectedPlacing: {
      type: Object,
      required: false
    },
    isCreating: {
      type: Boolean,
      required: false,
      default: false
    },
    entities: {
      type: Array,
      required: true
    },
  },
  data () {
    return {
      showModal: false,
      showConfirmationModal: false,
      isEditing: false,
      form: {
        data: {
          name: null,
          lat: null,
          lng: null
        },
        errors: new Errors()
      }
    }
  },
  mounted () {
    this.showModal = true
  },
  methods: {
    prefillForm: function () {
      this.form.data = { ...this.selectedPlacing }
    },
    handleCancel: function () {
      if (this.isEditing) {
        this.form.data = {
          name: null,
          lat: null,
          lng: null
        }
        this.isEditing = false
      } else {
        this.showModal = false
        setTimeout(() => {
          this.$emit('canceled')
        }, 250)
      }
    },
    handleDelete: function () {
      this.showConfirmationModal = true
      this.showModal = false
    },
    handleDestroyPlacing: function () {
      PlacingsService.delete(this.selectedPlacing.id).then(res => {
        this.showConfirmationModal = false
        this.showModal = false
        setTimeout(() => {
          this.$emit('deleted')
        }, 250)
      }).catch(err => {
        console.log(err)
        this.showConfirmationModal = false
        this.showModal = false
        this.showToast(this.$t('placings.delete_error'), this.$store.state.errorToastOptions)
      })
    },
    cancelDestroyPlacing: function () {
      this.showConfirmationModal = false
      this.showModal = true
    },
    handleOk: function () {
      if (this.isEditing) {
        var params = this.selectedPlacing
        params = { ...this.form.data }
        params.place_name = this.form.data.name
        PlacingsService.update(this.selectedPlacing.id, params).then(resp => {
          this.showModal = false
          setTimeout(() => {
            this.$emit('updated')
          }, 250)
        }).catch(err => {
          console.log(err)
          this.showModal = false
          setTimeout(() => {
            this.showToast(this.$t('placings.placing_update_error'), this.$store.state.errorToastOptions)
            this.$emit('canceled')
          }, 250)
        })
      } else if (this.isCreating) {
        if (this.form.data.entity === null || this.form.data.entity === '') {
          this.form.errors.set('entity')
        }
        let data = this.form.data
        data.place_name = this.form.data.name
        data.entity_id = this.form.data.entity.id
        PlacingsService.store(data).then(res => {
          this.showModal = false
          setTimeout(() => {
            this.$emit('created')
          }, 250)
        }).catch(err => {
          console.log(err)
          this.showModal = false
          setTimeout(() => {
            this.showToast(this.$t('placings.created_error'), this.$store.state.errorToastOptions)
            this.$emit('canceled')
          }, 250)
        })
      }
    },
    handleEdit: function () {
      this.prefillForm()
      this.isEditing = true
    }
  },
  watch: {
    invalidName: function (value) {
      if (value) {
        this.form.errors.set('name')
      } else {
        this.form.errors.clear('name')
      }
    },
    invalidLatitude: function (value) {
      if (value) {
        this.form.errors.set('lat')
      } else {
        this.form.errors.clear('lat')
      }
    },
    invalidLongitude: function (value) {
      if (value) {
        this.form.errors.set('lng')
      } else {
        this.form.errors.clear('lng')
      }
    }
  },
  computed: {
    canSubmit: function () {
      if (this.isEditing) {
        return !this.invalidLongitude && !this.invalidLatitude && !this.invalidName &&
          this.form.data.lat !== null && this.form.data.lat !== '' &&
          this.form.data.lng !== null && this.form.data.lng !== ''
      } else if (this.isCreating) {
        return !this.invalidLongitude && !this.invalidLatitude && !this.invalidName &&
          this.form.data.lat !== null && this.form.data.lat !== '' &&
          this.form.data.lng !== null && this.form.data.lng !== '' &&
          this.form.data.entity &&
          this.form.data.entity !== null && this.form.data.entity !== ''
      }
      return false
    },
    invalidName: function () {
      return this.form.data.name === null || this.form.data.name === ''
    },
    invalidLatitude: function () {
      if (this.form.data.lat) {
        return !String(this.form.data.lat).match(/^(\+|-)?(?:90(?:(?:\.0{1,7})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,8})?))$/)
      }
      return false
    },
    invalidLongitude: function () {
      if (this.form.data.lng) {
        return !String(this.form.data.lng).match(/^(\+|-)?(?:180(?:(?:\.0{1,6})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,8})?))$/)
      }
      return false
    },
  }
}
</script>

<style scoped>
.btn_gray {
  background-color: lightgrey!important;
}
</style>
